<template>
    <div class="optimizer-page">
        <v-row class="ma-0 pa-0">
            <v-col cols="12" lg="3">
                <v-card outlined class="optimizer-panel">
                    <v-card-title class="panel-title pb-2">Optimizer Controls</v-card-title>

                    <v-card-text>
                        <v-select
                            v-model="selected_audience"
                            class="mt-1"
                            :items="audienceOptions"
                            label="Audience"
                            outlined
                            dense
                            hide-details="auto"
                            :disabled="audienceOptions.length < 2"
                            @change="resetFrequencySlider"
                        />

                        <v-select
                            v-model="optimization_type"
                            class="mt-5"
                            :items="optimization_type_options"
                            label="Optimization Type"
                            outlined
                            dense
                            hide-details="auto"
                            :disabled="audienceOptions.length < 2 || selected_audience === '__all__'"
                            @change="handleOptimizationTypeChange"
                        />

                        <v-select
                            v-if="showFrequencySlider && frequencyGoalOptions.length"
                            v-model="selected_frequency_goal_id"
                            class="mt-5"
                            :items="frequencyGoalOptions"
                            label="Total Frequency Target"
                            outlined
                            dense
                            hide-details="auto"
                        />
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="12" lg="9" style="max-height: 62vh; overflow-y: scroll;">
                <v-row
                    v-if="optimizer_loading && !optimizer_result"
                    class="ma-0 pa-0 optimizer-loading"
                    align="center"
                    justify="center"
                >
                    <v-col cols="auto">
                        <v-progress-circular color="black" size="72" indeterminate />
                    </v-col>
                </v-row>

                <v-row v-else class="ma-0 pa-0">
                    <v-col v-if="optimizer_error" cols="12">
                        <v-alert dense text type="error">{{ optimizer_error }}</v-alert>
                    </v-col>

                    <v-col
                        v-for="audienceOptimization in visibleAudienceOptimizations"
                        :key="audienceOptimization.name"
                        cols="12"
                    >
                        <div class="audience-optimization-section">
                            <div class="audience-section-title">{{ audienceOptimization.name }}</div>
                            <v-row class="ma-0 pa-0">
                                <v-col cols="12" md="3">
                                    <div class="metric-block">
                                        <div class="metric-label">Optimized Reach</div>
                                        <div class="metric-value">{{ formatPercent(getSummary(audienceOptimization).reachRate) }}</div>
                                        <div class="metric-change" :class="changeClass(getSummary(audienceOptimization).reachRateChange)">
                                            {{ formatSignedPercent(getSummary(audienceOptimization).reachRateChange) }}
                                        </div>
                                    </div>
                                </v-col>

                                <v-col cols="12" md="3">
                                    <div class="metric-block">
                                        <div class="metric-label">Unique Reach</div>
                                        <div class="metric-value">{{ formatNumber(getSummary(audienceOptimization).reach) }}</div>
                                        <div class="metric-change" :class="changeClass(getSummary(audienceOptimization).reachChange)">
                                            {{ formatSignedPercent(getSummary(audienceOptimization).reachChange) }}
                                        </div>
                                    </div>
                                </v-col>

                                <v-col cols="12" md="3">
                                    <div class="metric-block">
                                        <div class="metric-label">Avg. Frequency</div>
                                        <div class="metric-value">{{ getSummary(audienceOptimization).frequency.toFixed(2) }}</div>
                                        <div class="metric-change" :class="changeClass(getSummary(audienceOptimization).frequencyChange)">
                                            {{ formatSignedPercent(getSummary(audienceOptimization).frequencyChange) }}
                                        </div>
                                    </div>
                                </v-col>

                                <v-col cols="12" md="3">
                                    <div class="metric-block">
                                        <div class="metric-label">Expected Overlap</div>
                                        <div class="metric-value">{{ formatPercent(getSummary(audienceOptimization).overlapRate) }}</div>
                                        <div class="metric-change" :class="changeClass(getSummary(audienceOptimization).overlapRateChange)">
                                            {{ formatSignedPercent(getSummary(audienceOptimization).overlapRateChange) }}
                                        </div>
                                    </div>
                                </v-col>
                                <v-col cols="12">
                                    <DataLevelTable
                                        class="optimizer-table"
                                        :tableHeader="optimizerTableHeader"
                                        :tableData="getOptimizedRows(audienceOptimization)"
                                        :selectedLevels="optimizerSelectedLevels"
                                        :numericColumns="optimizerNumericColumns"
                                        :decimalColumns="optimizerDecimalColumns"
                                        :currencyColumns="[]"
                                        :percentageColumns="optimizerPercentageColumns"
                                        stringCellMaxWidth="280px"
                                        mid
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <div class="optimizer-chart-panel">
                                        <div class="optimizer-chart-toolbar">
                                            <div class="optimizer-chart-title">Original vs Optimized</div>
                                            <v-chip-group
                                                v-model="comparison_metric"
                                                mandatory
                                                active-class="comparison-chip-active"
                                                class="comparison-chip-group"
                                            >
                                                <v-chip
                                                    v-for="metric in comparisonMetricOptions"
                                                    :key="metric.value"
                                                    :value="metric.value"
                                                    class="comparison-chip"
                                                    label
                                                    small
                                                >
                                                    {{ metric.text }}
                                                </v-chip>
                                            </v-chip-group>
                                        </div>
                                        <ORTOptimizationComparisonChart
                                            :chart_id="getChartId(audienceOptimization.name)"
                                            :chart_data="getComparisonChartData(audienceOptimization)"
                                            :chart_title="comparisonChartTitle"
                                            :metric_label="selectedComparisonMetric.text"
                                            :metric_type="selectedComparisonMetric.type"
                                        />
                                    </div>
                                </v-col>
                            </v-row>
                        </div>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import ort_reporting_api_caller from "@/javascript/ort_reporting.js";
import DataLevelTable from "@/components/Utility/DataLevelTable.vue";
import ORTOptimizationComparisonChart from "@/components/ORT/Charts/ORTOptimizationComparisonChart.vue";
import { ALERT_TYPE_ERROR, ALERT_GENERIC_MESSAGE } from "@/constants/constants";

export default {
    name: "ORTImpressionOptimizer",
    components: {
        DataLevelTable,
        ORTOptimizationComparisonChart
    },

    props: {
        portfolioData: {
            type: Array,
            default: () => []
        },
        levels: {
            type: Array,
            default: () => []
        },
        projectId: {
            type: String,
            default: ""
        },
        reportId: {
            type: [Number, String],
            required: true
        }
    },

    data: () => ({
        optimization_type: "maximize_reach",
        optimization_type_options: [
            { text: "Maximize Reach", value: "maximize_reach" },
            { text: "Total Frequency", value: "total_frequency" }
        ],
        selected_audience: "__all__",
        selected_frequency_goal_id: null,
        comparison_metric: "impressions",
        optimizer_loading: false,
        optimizer_result: null,
        optimizer_error: ""
    }),

    async mounted() {
        await this.fetchOptimization();
    },

    watch: {
        reportId: "fetchOptimization",
        projectId: "fetchOptimization",
        portfolioData: {
            handler() {
                this.fetchOptimization();
            },
            deep: true
        }
    },

    computed: {
        baselineTotalImpressions() {
            return this.portfolioData.reduce((sum, row) => {
                return sum + this.toNumber(row.impressions);
            }, 0);
        },

        targetImpressions() {
            return this.baselineTotalImpressions;
        },

        optimizationPlans() {
            const firstVisibleAudience = this.visibleAudienceOptimizations[0];
            return firstVisibleAudience ? firstVisibleAudience.plans : [];
        },

        audienceOptions() {
            if (!this.audienceOptimizations.length) {
                return [];
            }

            return [
                { text: "All Audiences", value: "__all__" },
                ...this.audienceOptimizations.map((optimization) => ({
                    text: optimization.name,
                    value: optimization.name
                }))
            ];
        },

        visibleAudienceOptimizations() {
            if (this.selected_audience === "__all__") {
                return this.audienceOptimizations;
            }

            return this.audienceOptimizations.filter((optimization) => {
                return optimization.name === this.selected_audience;
            });
        },

        audienceOptimizations() {
            const result = this.optimizer_result || {};
            const audienceOptimizations = result.audience_optimizations || {};
            const audienceEntries = Object.keys(audienceOptimizations).map((name) => {
                const normalized = this.normalizeOptimizationBlock(audienceOptimizations[name]);

                return {
                    name,
                    ...normalized
                };
            });

            if (audienceEntries.length) {
                return audienceEntries;
            }

            const normalized = this.normalizeOptimizationBlock(result);
            return normalized.plans.length ? [{ name: "Optimization", ...normalized }] : [];
        },

        comparisonMetricOptions() {
            return [
                { text: "Impressions", value: "impressions", type: "number" },
                { text: "Reach", value: "reach", type: "number" },
                { text: "Frequency", value: "frequency", type: "decimal" }
            ];
        },

        selectedComparisonMetric() {
            return (
                this.comparisonMetricOptions.find((metric) => metric.value === this.comparison_metric) ||
                this.comparisonMetricOptions[0]
            );
        },

        comparisonChartTitle() {
            return `${this.selectedComparisonMetric.text} Comparison`;
        },

        showFrequencySlider() {
            return this.optimization_type === "total_frequency";
        },

        frequencyPlans() {
            if (!this.showFrequencySlider) {
                return [];
            }

            return this.optimizationPlans
                .filter((plan) => plan.type === this.optimization_type)
                .sort((a, b) => this.getGoalValue(a) - this.getGoalValue(b));
        },

        frequencyGoalOptions() {
            return this.frequencyPlans.map((plan) => {
                const goalValue = this.getGoalValue(plan);

                return {
                    text: this.formatGoalLabel(goalValue),
                    value: plan.id
                };
            });
        },

        selectedOptimizationId() {
            if (!this.showFrequencySlider) {
                return "maximize_reach";
            }

            return this.selected_frequency_goal_id;
        },

        selectedPlanSummary() {
            return (
                this.optimizationPlans.find((plan) => {
                    return String(plan.id) === String(this.selectedOptimizationId);
                }) || {}
            );
        },

        optimizerTableHeader() {
            return [
                { text: "Face ID", value: "Face ID", sortable: true, desc: "" },
                { text: "Current Impr.", value: "current_impressions", sortable: true, desc: "" },
                { text: "Optimized Impr.", value: "optimized_impressions", sortable: true, desc: "" },
                { text: "Impr. Change %", value: "impressions_change_pct", sortable: true, desc: "" },
                { text: "Reach %", value: "estimate_reach_pct", sortable: true, desc: "" },
                { text: "Reach Pop", value: "estimate_reach_pop", sortable: true, desc: "" },
                { text: "Reach Change %", value: "estimate_reach_pct_change_pct", sortable: true, desc: "" },
                { text: "Frequency", value: "estimated_freq", sortable: true, desc: "" },
                { text: "Freq. Change %", value: "estimated_freq_change_pct", sortable: true, desc: "" }
            ];
        },

        optimizerSelectedLevels() {
            return ["Face ID"];
        },

        optimizerNumericColumns() {
            return [
                "current_impressions",
                "optimized_impressions",
                "impression_change",
                "estimate_reach_pop"
            ];
        },

        optimizerDecimalColumns() {
            return ["estimated_freq"];
        },

        optimizerPercentageColumns() {
            return [
                "impressions_change_pct",
                "estimate_reach_pct",
                "estimate_reach_pct_change_pct",
                "estimate_reach_pop_change_pct",
                "estimated_freq_change_pct",
                "estimated_overlap",
                "estimated_overlap_change_pct"
            ];
        },

    },

    methods: {
        normalizeOptimizationBlock(result = {}) {
            if (Array.isArray(result.optimizations) || result.optimization_data) {
                return {
                    plans: Array.isArray(result.optimizations) ? result.optimizations : [],
                    data: result.optimization_data || {}
                };
            }

            const groupedOptimizations = [
                ...(Array.isArray(result.maximized_reach) ? result.maximized_reach : []),
                ...(Array.isArray(result.total_frequency_targets) ? result.total_frequency_targets : [])
            ];

            const data = {};
            const plans = groupedOptimizations.map((optimization, index) => {
                const id = this.getOptimizationId(optimization, index);
                data[id] = {
                    summary: optimization.overall || {},
                    rows: Array.isArray(optimization.channels) ? optimization.channels : []
                };

                return {
                    id,
                    type: optimization.type,
                    goal: optimization.goal,
                    label: this.getOptimizationLabel(optimization)
                };
            });

            return { plans, data };
        },

        getSelectedPlanSummary(audienceOptimization) {
            return (
                (audienceOptimization.plans || []).find((plan) => {
                    return String(plan.id) === String(this.selectedOptimizationId);
                }) || {}
            );
        },

        getSelectedPlan(audienceOptimization) {
            if (!this.selectedOptimizationId) {
                return {};
            }

            return (audienceOptimization.data || {})[this.selectedOptimizationId] || {};
        },

        getOptimizedRows(audienceOptimization) {
            const selectedPlan = this.getSelectedPlan(audienceOptimization);

            return (selectedPlan.rows || []).map((row, index) => {
                const levelValues = row.levels ? Object.values(row.levels).filter(Boolean) : [];
                const currentImpressions = this.firstNumber(row, ["current_impressions", "currentImpressions"]);
                const optimizedImpressions = this.firstNumber(row, [
                    "optimized_impressions",
                    "optimal_impressions",
                    "optimizedImpressions",
                    "optimalImpressions"
                ]);
                const impressionChange = this.firstNumber(
                    row,
                    ["impression_change", "change"],
                    optimizedImpressions - currentImpressions
                );
                const estimatedReachPop = this.firstNumber(row, ["estimate_reach_pop", "estimated_reach_pop"]);
                const estimatedReachPopChange = this.firstNullablePercent(row, [
                    "estimate_reach_pop_change_pct",
                    "estimated_reach_pop_change_pct"
                ]);
                const estimatedFrequency = this.firstNumber(row, [
                    "estimated_freq",
                    "estimated_frequency",
                    "optimized_frequency",
                    "optimal_frequency"
                ]);
                const estimatedFrequencyChange = this.firstNullablePercent(row, [
                    "estimated_freq_change_pct",
                    "estimated_frequency_change_pct"
                ]);

                return {
                    id: `${row["Face ID"] || row.line_item || "line"}-${index}`,
                    "Face ID": row["Face ID"] || row.line_item || levelValues.join(" / ") || `Line Item ${index + 1}`,
                    channel: row.channel || "",
                    current_impressions: currentImpressions,
                    optimized_impressions: optimizedImpressions,
                    impression_change: impressionChange,
                    impressions_change_pct: this.firstNullablePercent(row, ["impressions_change_pct"]),
                    estimate_reach_pct: this.firstPercent(row, ["estimate_reach_pct", "estimated_reach_pct"]),
                    estimate_reach_pct_change_pct: this.firstNullablePercent(row, [
                        "estimate_reach_pct_change_pct",
                        "estimated_reach_change_pct"
                    ]),
                    estimate_reach_pop: estimatedReachPop,
                    estimate_reach_pop_change_pct: estimatedReachPopChange,
                    estimated_freq: estimatedFrequency,
                    estimated_freq_change_pct: estimatedFrequencyChange,
                    estimated_overlap: this.firstPercent(row, ["estimated_overlap"]),
                    estimated_overlap_change_pct: this.firstNullablePercent(row, ["estimated_overlap_change_pct"]),
                    original_reach_pop: this.firstNullableNumber(row, [
                        "original_reach_pop",
                        "current_reach_pop",
                        "estimated_original_reach_pop"
                    ]) ?? this.deriveOriginalValue(estimatedReachPop, estimatedReachPopChange),
                    original_freq: this.firstNullableNumber(row, [
                        "original_freq",
                        "current_freq",
                        "current_frequency",
                        "estimated_original_freq"
                    ]) ?? this.deriveOriginalValue(estimatedFrequency, estimatedFrequencyChange)
                };
            });
        },

        getComparisonChartData(audienceOptimization) {
            const metric = this.comparison_metric;

            return this.getComparisonOrderedRows(audienceOptimization)
                .map((row) => {
                    if (metric === "reach") {
                        return {
                            label: row["Face ID"],
                            original: row.original_reach_pop,
                            optimized: row.estimate_reach_pop
                        };
                    }

                    if (metric === "frequency") {
                        return {
                            label: row["Face ID"],
                            original: row.original_freq,
                            optimized: row.estimated_freq
                        };
                    }

                    return {
                        label: row["Face ID"],
                        original: row.current_impressions,
                        optimized: row.optimized_impressions
                    };
                });
        },

        getComparisonOrderedRows(audienceOptimization) {
            return [...this.getOptimizedRows(audienceOptimization)]
                .sort((a, b) => {
                    return (
                        Math.max(b.current_impressions, b.optimized_impressions) -
                        Math.max(a.current_impressions, a.optimized_impressions)
                    );
                })
                .slice(0, 12)
                .reverse();
        },

        getSummary(audienceOptimization) {
            const selectedPlan = this.getSelectedPlan(audienceOptimization);
            const planSummary = selectedPlan.summary || {};
            const card = this.getSelectedPlanSummary(audienceOptimization);

            return {
                reach: this.firstNumber(
                    planSummary,
                    ["reach_pop", "unique_reach", "optimized_reach_pop"],
                    this.toNumber(card.optimized_reach_pop)
                ),
                reachRate: this.firstPercent(
                    planSummary,
                    ["reach_pct", "optimized_reach", "optimized_reach_pct"],
                    card.optimized_reach_pct
                ),
                frequency: this.firstNumber(
                    planSummary,
                    ["freq", "average_frequency", "optimized_avg_freq"],
                    this.toNumber(card.optimized_avg_freq)
                ),
                overlapRate: this.firstPercent(
                    planSummary,
                    ["overlap", "expected_overlap", "optimized_expected_overlap"],
                    card.optimized_expected_overlap
                ),
                reachChange: this.firstNullableNumber(planSummary, ["reach_pop_change"]),
                reachRateChange: this.firstNullablePercent(planSummary, ["reach_pct_change"]),
                frequencyChange: this.firstNullableNumber(planSummary, ["freq_change"]),
                overlapRateChange: this.firstNullablePercent(planSummary, ["overlap_change"])
            };
        },

        getOptimizationId(optimization, index) {
            if (optimization.type === "maximize_reach") {
                return "maximize_reach";
            }

            return `${optimization.type}-${optimization.goal}-${index}`;
        },

        getOptimizationLabel(optimization) {
            if (optimization.type === "maximize_reach") {
                return "Maximize Reach";
            }

            return this.formatGoalLabel(optimization.goal);
        },

        formatGoalLabel(value) {
            if (value === null || value === undefined) {
                return "";
            }

            return Number(value) % 1 === 0 ? String(Number(value).toFixed(0)) : String(value);
        },
        async fetchOptimization() {
            if (!this.projectId || !this.reportId) {
                return;
            }

            this.optimizer_loading = true;
            this.optimizer_error = "";

            const payload = {
                project_id: this.projectId,
                report_id: this.reportId,
                target_impressions: this.targetImpressions || undefined
            };

            const token = await this.$auth.getTokenSilently();

            const result = await ort_reporting_api_caller
                .optimize_report(token, payload)
                .then((resp) => {
                    if (resp.success) {
                        return resp.data;
                    }

                    this.optimizer_error = this.getResponseMessage(resp);
                    return false;
                })
                .catch((e) => {
                    console.log("error", e);
                    this.optimizer_error = ALERT_GENERIC_MESSAGE;
                    return false;
                });

            if (result) {
                this.optimizer_result = result;
                this.ensureSelectedAudience();
                this.resetFrequencySlider();
            } else {
                this.$emit("triggerAlert", ALERT_TYPE_ERROR, this.optimizer_error || ALERT_GENERIC_MESSAGE);
            }

            this.optimizer_loading = false;
        },

        handleOptimizationTypeChange() {
            this.resetFrequencySlider();
        },

        ensureSelectedAudience() {
            if (this.selected_audience === "__all__") {
                return;
            }

            const selectedStillExists = this.audienceOptimizations.some((optimization) => {
                return optimization.name === this.selected_audience;
            });

            if (!selectedStillExists) {
                this.selected_audience = "__all__";
            }
        },

        resetFrequencySlider() {
            if (this.selected_audience === "__all__") {
                this.optimization_type = "maximize_reach";
                this.selected_frequency_goal_id = null;
                return;
            }

            if (!this.showFrequencySlider || !this.frequencyPlans.length) {
                this.selected_frequency_goal_id = null;
                return;
            }

            const selectedStillExists = this.frequencyPlans.some((plan) => {
                return String(plan.id) === String(this.selected_frequency_goal_id);
            });

            if (!selectedStillExists) {
                this.selected_frequency_goal_id = this.frequencyPlans[0].id;
            }
        },

        getGoalValue(plan) {
            if (!plan) {
                return null;
            }

            if (plan.goal !== undefined && plan.goal !== null && typeof plan.goal !== "object") {
                return this.toNumber(plan.goal);
            }

            if (plan.goal) {
                const goalValue =
                    plan.goal.value ??
                    plan.goal.goal ??
                    plan.goal.target ??
                    plan.goal.frequency ??
                    plan.goal.weekly_frequency ??
                    plan.goal.total_frequency;

                if (goalValue !== undefined && goalValue !== null) {
                    return this.toNumber(goalValue);
                }
            }

            const idMatch = String(plan.label || "").match(/(\d+(\.\d+)?)$/);

            if (idMatch) {
                return this.toNumber(idMatch[1]);
            }

            return null;
        },

        getChartId(audienceName) {
            const sanitizedAudienceName = String(audienceName || "audience")
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "_")
                .replace(/^_+|_+$/g, "");

            return `ort_optimization_comparison_chart_${sanitizedAudienceName || "audience"}`;
        },

        getResponseMessage(resp) {
            if (!resp || !resp.message) {
                return ALERT_GENERIC_MESSAGE;
            }

            try {
                const parsedMessage = JSON.parse(resp.message);
                return parsedMessage.notes || parsedMessage.message || resp.message;
            } catch (e) {
                return resp.message;
            }
        },

        firstNumber(row, keys, fallback = 0) {
            for (let key of keys) {
                if (row && row[key] !== undefined && row[key] !== null && row[key] !== "") {
                    return this.toNumber(row[key]);
                }
            }

            return this.toNumber(fallback);
        },

        firstNullableNumber(row, keys) {
            for (let key of keys) {
                if (row && row[key] !== undefined && row[key] !== null && row[key] !== "") {
                    return this.toNumber(row[key]);
                }
            }

            return null;
        },

        firstPercent(row, keys, fallback = 0) {
            for (let key of keys) {
                if (row && row[key] !== undefined && row[key] !== null && row[key] !== "") {
                    return this.normalizePercentValue(row[key]);
                }
            }

            return this.normalizePercentValue(fallback);
        },

        firstNullablePercent(row, keys) {
            const value = this.firstNullableNumber(row, keys);
            return value === null ? null : this.normalizePercentValue(value);
        },

        deriveOriginalValue(optimizedValue, changePct) {
            if (optimizedValue === null || optimizedValue === undefined || changePct === null || changePct === undefined) {
                return null;
            }

            const denominator = 1 + this.normalizePercentValue(changePct);

            if (denominator === 0) {
                return null;
            }

            return optimizedValue / denominator;
        },

        toNumber(value) {
            if (value === null || value === undefined || value === "") {
                return 0;
            }

            if (typeof value === "number") {
                return value;
            }

            return Number(String(value).replace(/[%,$\s]/g, "")) || 0;
        },

        formatNumber(value) {
            return Intl.NumberFormat("en", {
                maximumFractionDigits: 0
            }).format(value || 0);
        },

        formatSignedNumber(value) {
            if (value === null || value === undefined) {
                return "n/a";
            }

            const formatted = this.formatNumber(Math.abs(value));
            return value >= 0 ? `+${formatted}` : `-${formatted}`;
        },

        formatSignedDecimal(value) {
            if (value === null || value === undefined) {
                return "n/a";
            }

            const formatted = Math.abs(value).toFixed(2);
            return value >= 0 ? `+${formatted}` : `-${formatted}`;
        },

        normalizePercentValue(value) {
            const numberValue = this.toNumber(value);
            return numberValue;
        },

        formatPercent(value) {
            return `${((value || 0) * 100).toFixed(1)}%`;
        },

        formatSignedPercent(value) {
            if (value === null || value === undefined) {
                return "n/a";
            }

            const formatted = `${(Math.abs(this.normalizePercentValue(value)) * 100).toFixed(1)}%`;
            return value >= 0 ? `+${formatted}` : `-${formatted}`;
        },

        changeClass(value) {
            if (value === null || value === undefined || value === 0) {
                return "neutral-cell";
            }

            return value > 0 ? "positive-cell" : "negative-cell";
        },

        formatPercentValue(value) {
            return this.formatPercent(this.normalizePercentValue(value));
        }
    }
};
</script>

<style scoped>
.optimizer-page {
    width: 100%;
}

.optimizer-panel {
    border-radius: 8px;
    border-color: black;
    height: 62vh;
}

.panel-title {
    font-family: "Poppins";
    font-size: 1rem;
    font-weight: 600;
}

.metric-block {
    min-height: 86px;
    padding: 8px 0;
}

.metric-label {
    color: rgba(0, 0, 0, 0.58);
    font-size: 0.78rem;
    font-weight: 600;
    text-transform: uppercase;
}

.metric-value {
    font-family: "Poppins";
    font-size: 1.6rem;
    font-weight: 700;
    line-height: 1.4;
}

.metric-change {
    font-size: 0.82rem;
    font-weight: 600;
}

.audience-optimization-section {
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    padding: 8px 0 24px;
}

.audience-optimization-section:last-child {
    border-bottom: 0;
}

.audience-section-title {
    color: rgba(0, 0, 0, 0.78);
    font-family: "Poppins";
    font-size: 1rem;
    font-weight: 700;
    padding: 0 0 6px;
}

.optimizer-chart-panel {
    padding: 8px 0 0;
}

.optimizer-chart-toolbar {
    align-items: center;
    display: flex;
    gap: 12px;
    justify-content: space-between;
    min-height: 40px;
}

.optimizer-chart-title {
    color: rgba(0, 0, 0, 0.72);
    font-family: "Poppins";
    font-size: 0.95rem;
    font-weight: 600;
}

.comparison-chip-group {
    flex: 0 0 auto;
}

.comparison-chip {
    border: 1px solid rgba(0, 0, 0, 0.18) !important;
    border-radius: 4px !important;
    font-weight: 600;
}

.comparison-chip-active {
    background-color: #111111 !important;
    color: #ffffff !important;
}

.optimizer-table {
    border: 0;
    border-radius: 0;
}

.empty-table-cell {
    color: rgba(0, 0, 0, 0.56);
    font-style: italic;
    height: 80px;
}

.line-name {
    font-weight: 600;
    max-width: 420px;
}

.line-detail {
    color: rgba(0, 0, 0, 0.55);
    font-size: 0.78rem;
    max-width: 420px;
}

.positive-cell {
    color: #137333;
}

.negative-cell {
    color: #b3261e;
}

.neutral-cell {
    color: rgba(0, 0, 0, 0.55);
}
</style>
